import { useContext, useState } from "react";
import { ArrowRight, GitBranch, PencilLine, Rocket } from "lucide-react";
import { AuthContext } from "../../../../../../../context/AuthContext";
import { useAlert } from "../../../../../../../context/AlertContext";
import ConfirmDialogue from "../../../../../../../components/ui/ConfirmDialogue";
import { Button, Pill } from "../../../../components/ui";
import { fmtDateTime } from "../../../../utils/format";
import { publishConfigDraft } from "../../../../mock/estimateService.mock";
import SectionShell from "../SectionShell";

const STATUS = {
  ACTIVE: { tone: "success", label: "Active" },
  DRAFT: { tone: "warning", label: "Draft" },
  ARCHIVED: { tone: "neutral", label: "Archived" },
};

const FLOW = ["Edit any section", "Changes collect in a draft version", "Publish → new estimates use it"];

export default function VersionsSection({ versions, readOnly, onSaved }) {
  const { user } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const sorted = [...versions].sort((a, b) => b.version - a.version);
  const draft = versions.find((v) => v.status === "DRAFT");
  const active = versions.find((v) => v.status === "ACTIVE");

  const handleConfirm = async (ok) => {
    setConfirmOpen(false);
    if (!ok) return;
    setPublishing(true);
    try {
      const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Admin";
      const v = await publishConfigDraft(name);
      showAlert({ type: "success", title: "Published (mock)", message: `${v.label} is now the active configuration.` });
      await onSaved?.();
    } catch (e) {
      showAlert({ type: "error", message: e.message || "Publish failed" });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <SectionShell
      title="Versions"
      subtitle="Estimates are pinned to the config version they were calculated with."
      icon={GitBranch}
      readOnly={readOnly}
      actions={
        !readOnly && (
          <Button variant="primary" size="sm" icon={Rocket} disabled={!draft || publishing} onClick={() => setConfirmOpen(true)}>
            {publishing ? "Publishing…" : draft ? `Publish ${draft.label}` : "No draft to publish"}
          </Button>
        )
      }
    >
      <ol className="mb-5 flex flex-wrap items-center gap-2 rounded-[4px] border border-border_soft bg-surface_alt px-3 py-2.5 text-[12px] text-text_secondary" aria-label="How versions work">
        {FLOW.map((step, i) => (
          <li key={step} className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10.5px] font-semibold text-white">{i + 1}</span>
            {step}
            {i < FLOW.length - 1 && <ArrowRight size={13} className="text-text_muted" aria-hidden="true" />}
          </li>
        ))}
      </ol>

      <ol className="relative ml-2 border-l border-border pl-5">
        {sorted.map((v) => {
          const st = STATUS[v.status] || STATUS.ARCHIVED;
          return (
            <li key={v.id} className="relative pb-5 last:pb-0">
              <span
                className={`absolute -left-[27px] top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-surface ${
                  v.status === "ACTIVE" ? "bg-success" : v.status === "DRAFT" ? "bg-warning" : "bg-border_strong"
                }`}
                aria-hidden="true"
              />
              <div className="rounded-[4px] border border-border bg-surface px-3.5 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13.5px] font-semibold text-text_primary">{v.label}</span>
                  <Pill tone={st.tone} dot>
                    {st.label}
                  </Pill>
                  {v.status === "DRAFT" && (
                    <span className="inline-flex items-center gap-1 text-[11.5px] text-text_muted">
                      <PencilLine size={11} aria-hidden="true" /> based on {active?.label || "active version"}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[12.5px] text-text_secondary">{v.notes || "—"}</p>
                <p className="mt-1.5 text-[11.5px] text-text_muted">
                  {v.publishedAt ? `Published by ${v.publishedBy || "—"} · ${fmtDateTime(v.publishedAt)}` : "Not published yet"}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      {confirmOpen && (
        <ConfirmDialogue
          confirmData={{
            title: `Publish ${draft?.label}?`,
            message: `${draft?.label} becomes the active configuration for all new estimates. ${active?.label || "The current version"} will be archived. Existing estimates keep their version.`,
            confirmText: "Publish",
            variant: "primary",
            icon: <Rocket size={20} />,
          }}
          handleConfirmClose={handleConfirm}
        />
      )}
    </SectionShell>
  );
}
